/**
 * Wave A — Inquiry & measurement commands. All are pure read-only math over
 * the current selection / a picked point, reported to the command line.
 * (docs/architecture/command-parity-plan.md)
 */

import { requireSelection, entityToPolyline, runInteraction, PT } from './lib';
import { useAppStore } from '../../../state/store';
import { polygonArea, polylineLength, meshArea, massProperties } from '../geometryMath';
import type { Pt3 } from '../geometryMath';
import type { RunnerMap } from '../runnerRegistry';

function pickTwo(label: string, compute: (a: Pt3, b: Pt3) => string): string {
    runInteraction({
        steps: [
            { prompt: `${label}: specify first point`, kind: 'point' },
            { prompt: `${label}: specify second point`, kind: 'point' },
        ],
        complete: (pts) => compute([pts[0].x, pts[0].y, pts[0].z], [pts[1].x, pts[1].y, pts[1].z]),
    });
    return `${label}: picking…`;
}

function solidMeshes(list: ReturnType<typeof requireSelection>) {
    return (list ?? [])
        .filter((e) => e.type === 'solid')
        .map((e) => ({
            vertices: (e.data.vertices as number[][]) || [],
            indices: (e.data.indices as number[][]) || [],
        }));
}

export const queryRunners: RunnerMap = {
    'inq.distance': () => pickTwo('Distance', (a, b) => {
        const d = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2 + (b[2] - a[2]) ** 2);
        const angle = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
        return `Distance = ${d.toFixed(4)}, Angle in XY = ${angle.toFixed(2)}°, ΔX=${(b[0] - a[0]).toFixed(3)}, ΔY=${(b[1] - a[1]).toFixed(3)}, ΔZ=${(b[2] - a[2]).toFixed(3)}`;
    }),
    'inq.measureGeom': () => pickTwo('Measure', (a, b) => {
        const d = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2 + (b[2] - a[2]) ** 2);
        return `Distance = ${d.toFixed(4)} (live quick measure)`;
    }),
    'inq.id': () => {
        runInteraction({
            steps: [PT('ID: specify point')],
            complete: (pts) => {
                const p = pts[0];
                return `Point X = ${p.x.toFixed(4)}, Y = ${p.y.toFixed(4)}, Z = ${p.z.toFixed(4)}`;
            },
        });
        return 'ID: picking…';
    },
    'inq.length': () => {
        const list = requireSelection('Length');
        if (!list) return 'Length: nothing selected.';
        let total = 0;
        let counted = 0;
        list.forEach((e) => {
            const poly = entityToPolyline(e);
            if (poly) { total += polylineLength(poly); counted++; }
        });
        return `Total length of ${counted} object(s): ${total.toFixed(4)}`;
    },
    'inq.area': () => {
        const list = requireSelection('Area');
        if (!list) return 'Area: nothing selected.';
        const meshes = solidMeshes(list);
        if (meshes.length > 0) {
            const total = meshes.reduce((sum, m) => sum + meshArea(m), 0);
            return `Total surface area: ${total.toFixed(4)}`;
        }
        let total = 0;
        list.forEach((e) => {
            const poly = entityToPolyline(e);
            if (poly && poly.length >= 3) total += polygonArea(poly);
        });
        return `Total area of ${list.length} closed object(s): ${total.toFixed(4)}`;
    },
    'inq.volume': () => {
        const list = requireSelection('Volume');
        if (!list) return 'Volume: nothing selected.';
        const meshes = solidMeshes(list);
        if (meshes.length === 0) return 'Volume: no solid objects selected.';
        const total = meshes.reduce((sum, m) => sum + massProperties(m).volume, 0);
        return `Total volume of ${meshes.length} solid(s): ${total.toFixed(4)}`;
    },
    'inq.massProp': () => {
        const list = requireSelection('MassProperties');
        if (!list) return 'MassProperties: nothing selected.';
        const solid = list.find((e) => e.type === 'solid');
        if (!solid) return 'MassProperties: select a solid object.';
        const mp = massProperties({
            vertices: (solid.data.vertices as number[][]) || [],
            indices: (solid.data.indices as number[][]) || [],
        });
        return `Volume=${mp.volume.toFixed(4)}, SurfaceArea=${mp.area.toFixed(4)}, Centroid=(${mp.centroid.map((v) => v.toFixed(3)).join(', ')})`;
    },
    'inq.list': () => {
        const list = requireSelection('List');
        if (!list) return 'List: nothing selected.';
        const lines = list.map((e) => {
            const d = e.data as Record<string, unknown>;
            const extra = e.type === 'line' && Array.isArray(d.start) && Array.isArray(d.end)
                ? `, length=${polylineLength([d.start as Pt3, d.end as Pt3]).toFixed(3)}` : '';
            return `${e.type} "${e.name}" layer=${e.layer} id=${e.id.slice(0, 12)}${extra}`;
        });
        return lines.join(' | ');
    },
    'inq.bcount': () => {
        const { entities } = useAppStore.getState();
        const counts = new Map<string, number>();
        Object.values(entities).forEach((e) => {
            if (e.type === 'block' || e.type === 'insert') {
                const key = String((e.data as Record<string, unknown>).blockName ?? 'anonymous');
                counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        });
        if (counts.size === 0) return 'BlockCount: no blocks in the drawing.';
        return [...counts.entries()].map(([k, n]) => `${k} x${n}`).join(', ');
    },
};
